import { useEffect, useState } from 'react'

/**
 * Routeur minimal, sans dépendance.
 *
 * Quatre destinations ne justifient pas d'ajouter une bibliothèque à une stack
 * décrite comme « actée à 100 % ». Ce qui compte ici, c'est que le **bouton
 * retour physique d'Android** fonctionne : sans historique, il ferme l'app au
 * lieu de revenir en arrière, ce qui est déroutant pour un public peu habitué
 * aux applications.
 *
 * L'API History plutôt que le hash : `navigateFallback: '/index.html'` est déjà
 * configuré dans vite.config.ts, donc un rechargement direct sur /transactions
 * fonctionne aussi bien servi par Cloudflare Pages que par le service worker,
 * une fois l'app installée.
 *
 * À remplacer par un vrai routeur le jour où il faudra des paramètres d'URL ou
 * des routes imbriquées — pas avant.
 */

export const ROUTES = ['/', '/transactions', '/budget', '/profil', '/fitiavana'] as const

export type Route = (typeof ROUTES)[number]

/**
 * Les routes qui vivent HORS de l'app.
 *
 * `/fitiavana` est le panneau d'administration : 100 % online, sans navigation
 * basse, et surtout **sans compte utilisateur** — un administrateur n'en a pas.
 * Il doit donc court-circuiter le portail d'entrée, sinon on ne pourrait
 * jamais ouvrir le premier compte.
 */
export const STANDALONE_ROUTES: readonly Route[] = ['/fitiavana']

function currentRoute(): Route {
  const path = window.location.pathname as Route
  return ROUTES.includes(path) ? path : '/'
}

export function navigate(route: Route) {
  if (route === currentRoute()) return
  window.history.pushState({}, '', route)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute)

  useEffect(() => {
    const onChange = () => setRoute(currentRoute())
    window.addEventListener('popstate', onChange)
    return () => window.removeEventListener('popstate', onChange)
  }, [])

  return route
}
